import React, {useEffect, useState} from 'react'
import {DateTime} from 'luxon'
import {IChannel, ICompetition, IMaxOdd, IProvider, oddsFormats, IOdds as IOddsMain} from '../utils/types'
import bn from '../utils/bemnames'
import {getFullCompetitionName} from "../utils/competition"
import {AllowedSportsForMCWidget} from "../utils/match-center"
import {ISport, IEvent, IOutcomeNameGroup} from "../utils/types"
import OddsComparisonEventDateAndName from "./OddsComparisonEventDateAndName"
import {slugify} from '../utils/helpers'
import OddsGroup from './OddsComparison/OddsGroup'
import {getProviderImageUrl, LogoType} from "../utils/providers"
import marketTypes from "../utils/market-types"
import {OutcomeIds} from "../utils/outcome"
import {getOutLink} from "../utils/odds"
import OddsTrackingLink from './OddsTrackingLink'
import {getEventLink} from "../utils/event"
import {useGlobalState} from "../contexts/GlobalState";
import OddsButtonWithoutOperator from "./Buttons/OddsButtonWithoutOperator";

const bem = bn.create('odds-comp')

interface IProps {
  competitions: ITableCompetition[],
  oddsFormat: oddsFormats,
  outLinkFolder?: string | null,
  isMobile: boolean,
  clickableRows: boolean,
  showTeamLogos: boolean,
  linkTemplate: string | null,
  sports: ISport[],
  format: string,
  period: number,
  marketType: number,
  channel?: IChannel | null,
  logoType: string,
  selCompetitionId: number | null
}

export interface IHeaderProvider {
  id: number,
  name: string,
  slug: string,
  themeColor: string
}

export interface ITableCompetition extends ICompetition {
  headerProviders: IHeaderProvider[],
  outcomeNames: IOutcomeNameGroup[],
  events: IEventGroup[],
}

interface IEventGroup {
  date: string,
  events: IOddsComparisonTableEvent[],
}

export interface IProvider2Odds {
  [key: number]: IOdds[]
}

export interface IOddsComparisonTableEvent extends IEvent {
  channels: IChannel[] | null,
  homeName: string,
  awayName: string,
  homeRegionName: string | null,
  awayRegionName: string | null,
  homeIsNationalTeams: boolean,
  awayIsNationalTeams: boolean,
  providerId2Odds: IProvider2Odds,
  maxOdds: IMaxOdd[],
  ouOdds: IMaxOdd[],
  maxOddsMarketId: number,
  homeLogo: string | null,
  awayLogo: string | null,
}

export interface IMatchScheduleTableEvent extends IOddsComparisonTableEvent {
  score: number[],
  gameMoment: string | null,
}

export interface IOdds extends IOddsMain {
  odds: number,
  oddsPrev: number,
  outcomeId: number,
  line: string,
  startOdds: number | null,
  provider: IProvider
}

enum OutcomeType {
  HOME = 0,
  AWAY = 1,
  DRAW = 2,
}

const OddsComparisonTable: React.FC<IProps> = (props) => {
  const {
    competitions,
    oddsFormat,
    outLinkFolder = undefined,
    isMobile,
    clickableRows,
    showTeamLogos,
    linkTemplate,
    sports,
    format,
    period,
    marketType,
    logoType,
    selCompetitionId
  } = props

  const [competition, setCompetition] = useState<ITableCompetition | null>(null)
  const [sport, setSport] = useState<ISport | null>(null)

  const {globalState} = useGlobalState()

  const isClickableEvent = (sportId: number): boolean => {
    return !!(clickableRows && linkTemplate && Object.values(AllowedSportsForMCWidget).includes(sportId));
  }

  const getLine = (oddObject: IOdds | IMaxOdd): string => {
    let prefix = ''
    if ('outcomeName' in oddObject) {
      prefix = oddObject.outcomeName[0].toLocaleLowerCase()
    } else {
      if (oddObject.outcomeId === OutcomeIds.OUTCOME_OVER)
        prefix = 'o'
      else if (oddObject.outcomeId === OutcomeIds.OUTCOME_UNDER)
        prefix = 'u'
    }

    return prefix + oddObject.line
  }

  const getProviderIndex = (headerProviders: IProvider[], providerId: number | null): number | null => {
    return headerProviders.findIndex((item) => {
      return item.id === providerId
    });
  }

  const scrollTable = (direction: string) => {
    const tableElement = document.getElementById('us-table')
    if (tableElement) {
      if (direction === 'r')
        tableElement.scrollLeft += 50
      else tableElement.scrollLeft -= 50
    }
  }

  const getOutcomeNames = (): IOutcomeNameGroup | undefined => {
    return competition?.outcomeNames.find((item) => item.marketTypeId === marketType)
  }

  const isOddsAvailable = (event: IOddsComparisonTableEvent, providerId: number, teamIndex: number): boolean => {
    let result = false
    if (event.providerId2Odds[providerId] !== undefined && event.providerId2Odds[providerId][marketType] !== undefined
      && event.providerId2Odds[providerId][marketType][period] !== undefined
      && event.providerId2Odds[providerId][marketType][period][teamIndex] !== undefined) {
      result = true
    }

    return result
  }

  const getTeamRowLayout = (eventData: IOddsComparisonTableEvent, outcomeType: OutcomeType): JSX.Element | null => {
    if (!competition || globalState.widget === null)
      return null

    let outcomeNames = getOutcomeNames()
    let teamLogo, teamName = null
    let teamIndex: number = 0
    if (outcomeType === OutcomeType.HOME) {
      teamLogo = eventData.homeLogo
      teamName = eventData.homeName
      teamIndex = 0
    } else if (outcomeType === OutcomeType.AWAY) {
      teamLogo = eventData.awayLogo
      teamName = eventData.awayName
      teamIndex = outcomeNames !== undefined && outcomeNames.names.length > 2 ? 2 : 1
    } else {
      teamName = 'DRAW'
      teamIndex = 1
    }

    return (
      <div className={bem.e('us-table-row__odds-cell__child-row')}>
        <div className={bem.e('us-table-row__cell team')}>
          {teamLogo && !isMobile && (
            <img src={teamLogo} className={bem.e('team-logo')} alt="logo"/>
          )}
          <span>{teamName}</span>
        </div>
        <React.Fragment>
          <div className={bem.e('us-table-row__cell odds')}>
            {eventData.maxOdds[marketType] && eventData.maxOdds[marketType][teamIndex].odds != null && (
              <React.Fragment>
                {marketType === marketTypes.MT_OU_ID && (
                  <div>{getLine(eventData.maxOdds[marketType][teamIndex])}</div>
                )}
                {marketType === marketTypes.MT_AH_ID && eventData.maxOdds[marketType][teamIndex].startOdds && eventData.maxOdds[marketType][teamIndex].provider && (
                  <OddsButtonWithoutOperator
                    odds={eventData.maxOdds[marketType][teamIndex]}
                    provider={eventData.maxOdds[marketType][teamIndex].provider}
                    oddsFormat={oddsFormat}
                    outLinkPath={outLinkFolder}
                    oddsKey={'startOdds'}
                    customClassName={bem.e('us-table-row__odds-button')}
                    widgetId={globalState.widget.id}
                  />
                )}
                {eventData.maxOdds[marketType][teamIndex].provider && (
                  <OddsButtonWithoutOperator
                    odds={eventData.maxOdds[marketType][teamIndex]}
                    provider={eventData.maxOdds[marketType][teamIndex].provider}
                    oddsFormat={oddsFormat}
                    outLinkPath={outLinkFolder}
                    customClassName={bem.e('us-table-row__odds-button')}
                    widgetId={globalState.widget.id}
                  />
                )}
              </React.Fragment>
            )}
          </div>
          {competition.headerProviders.map((provider) => (
            <div className={bem.e('us-table-row__cell odds')} key={eventData.id + provider.id}>
              {isOddsAvailable(eventData, provider.id, teamIndex) && globalState.widget && (
                <div className={bem.e('us-table-row__cell__odds-container')}>
                  {marketType === marketTypes.MT_OU_ID && (
                    <span>{getLine(eventData.providerId2Odds[provider.id][marketType][period][teamIndex])}</span>
                  )}
                  {marketType === marketTypes.MT_AH_ID && eventData.providerId2Odds[provider.id][marketType][period][teamIndex].startOdds && (
                    <OddsButtonWithoutOperator
                      odds={eventData.providerId2Odds[provider.id][marketType][period][teamIndex]}
                      provider={eventData.providerId2Odds[provider.id][marketType][period][teamIndex].provider}
                      oddsFormat={oddsFormat}
                      outLinkPath={outLinkFolder}
                      oddsKey={'startOdds'}
                      customClassName={bem.e('us-table-row__odds-button')}
                      widgetId={globalState.widget.id}
                    />
                  )}
                  <OddsButtonWithoutOperator
                    odds={eventData.providerId2Odds[provider.id][marketType][period][teamIndex]}
                    provider={eventData.providerId2Odds[provider.id][marketType][period][teamIndex].provider}
                    oddsFormat={oddsFormat}
                    outLinkPath={outLinkFolder}
                    customClassName={bem.e('us-table-row__odds-button')}
                    widgetId={globalState.widget.id}
                  />
                </div>
              )}
            </div>
          ))}
        </React.Fragment>
      </div>
    )
  }

  useEffect(() => {
    if (competitions.length > 0) {
      let competitionIndex = competitions.findIndex((item) => item.id === selCompetitionId)
      if (competitionIndex !== -1) {
        setCompetition(competitions[competitionIndex])
        let sport = sports.find(sport => competitions[competitionIndex].sportId === sport.id) || null
        setSport(sport)
      }
    }
  }, [competitions, selCompetitionId, sports])

  if (competitions.length) {
    if (format === 'eu') {
      return (
        <div>
          {competitions.map(competition => {
            return (
              <div className={bem.e('table-wrp')} key={competition.id}>
                <div className={bem.e('table-header')}>
                  <div>
                    {getFullCompetitionName(competition)}
                  </div>
                </div>
                {competition.events.map(eventGroup => {
                    return (
                      <React.Fragment key={eventGroup.date}>
                        <div className={bem.e('date-time')}>
                            <span>{DateTime.fromISO(eventGroup.date).toLocaleString({
                              day: 'numeric',
                              month: 'short',
                              weekday: 'long'
                            })}</span>
                          <div className={bem.e('outcome-names-group-title-container')}>
                            <div className={bem.e('outcome-names-line')}>
                              {competition.outcomeNames.map((outcomeNameDetails, idx) => {
                                // Do not show over/under in mobile
                                if (!isMobile || idx === 0) {
                                  return (
                                    <div
                                      className={bem.e('outcome-names-group-container')}
                                      key={'outcomeGroup' + idx}
                                    >
                                      {isMobile ?
                                        ''
                                        :
                                        <div
                                          className={bem.e('outcome-names-group-title') + ' ' + bem.e('outcome-width-' + outcomeNameDetails.names.length + 'x')}>
                                          <span>{outcomeNameDetails.title}</span>
                                        </div>}
                                    </div>
                                  )
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        </div>
                        <div className={bem.e('outcome-names-line')}>
                          {competition.outcomeNames.map((outcomeNameDetails, idx) => {
                            // Do not show over/under in mobile
                            if (!isMobile || idx === 0) {
                              return (
                                <div className={bem.e('outcome-names-group-container')} key={'outcomeNameDetail' + idx}>
                                  <div className={bem.e('outcome-names-group')}>
                                    {outcomeNameDetails.names.map((outcomeName, index) => (
                                      <div key={outcomeName + '-' + index} className={bem.e('outcome-name')}>
                                        {outcomeName}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            }
                            return null;
                          })}
                        </div>
                        <div>
                          {eventGroup.events.map(event => {
                            if (sport === null)
                              return null

                            return (
                              <div className={bem.e('event-wrapper')} key={event.id}>
                                {isClickableEvent(competition.sportId) && linkTemplate && competition.region?.name ? (
                                  <a href={getEventLink(
                                    linkTemplate,
                                    event.id,
                                    slugify(event.name),
                                    slugify(sport.name),
                                    slugify(competition.name),
                                    slugify(competition.region.name),
                                    slugify(competition.region.trName),
                                    slugify(sport.trName)
                                  )}
                                     rel="noreferrer"
                                     className={bem.e('event-date-and-name-wrapper')}
                                  >
                                    <OddsComparisonEventDateAndName
                                      event={event}
                                      showTeamLogos={showTeamLogos}
                                      isMobile={isMobile}
                                    />
                                  </a>
                                ) : (
                                  <div className={bem.e('event-date-and-name-wrapper')}>
                                    <OddsComparisonEventDateAndName
                                      event={event}
                                      showTeamLogos={showTeamLogos}
                                      isMobile={isMobile}
                                    />
                                  </div>
                                )}
                                {event.maxOdds ? <OddsGroup
                                  oddsGroup={event.maxOdds}
                                  event={event}
                                  headerProviders={competition.headerProviders}
                                  oddsFormat={oddsFormat}
                                  outLinkFolder={outLinkFolder}
                                  logoType={logoType}
                                /> : ''}
                                {!isMobile && event.ouOdds ? <OddsGroup
                                  oddsGroup={event.ouOdds}
                                  event={event}
                                  headerProviders={competition.headerProviders}
                                  oddsFormat={oddsFormat}
                                  outLinkFolder={outLinkFolder}
                                  logoType={logoType}
                                /> : ''}
                              </div>
                            )
                          })}
                        </div>
                      </React.Fragment>
                    )
                  }
                )}
              </div>
            )
          })}
        </div>
      )
    } else if (format === 'us' && competition && sport) {
      return (
        <div>
          {/* Day containers */}
          {competition.events.map((eventGroup) => (
            <div key={eventGroup.date}>
              <div className={bem.e('us-date-time')}>
                <span>
                  {DateTime.fromISO(eventGroup.date).toLocaleString({
                    day: 'numeric',
                    month: 'short',
                    weekday: 'long',
                    year: 'numeric'
                  })}{!isMobile ? ` - ${sport.name}` : ''}
                </span>
              </div>
              <div className={bem.e('us-table')} id="us-table">
                <div className={bem.e('us-table-header')}>
                  {!isMobile && (
                    <div className={bem.e('us-table-header_cell time')}>
                      <span>TIME</span>
                    </div>
                  )}
                  <div className={bem.e('us-table-header_cell team')}>
                    {!isMobile && (
                      <span>
                      {DateTime.fromISO(eventGroup.date).toLocaleString({
                        day: 'numeric',
                        month: 'short',
                        weekday: 'short',
                        year: 'numeric'
                      })}
                    </span>
                    )}
                  </div>
                  <div className={bem.e('us-table-header_cell odd')}>OPENER</div>
                  {competition.headerProviders.map((provider) => {
                    return (
                      <div
                        className={bem.e('us-table-header_cell odd')}
                        key={provider.id}
                        style={{backgroundColor: provider.themeColor}}
                      >
                        <OddsTrackingLink
                          outLink={getOutLink(outLinkFolder, competition.headerProviders[getProviderIndex(competition.headerProviders, provider.id) ?? 0].toString())}
                          rel={'noreferrer'}
                          target={'_blank'}
                          providerId={provider.id}
                          widgetId={globalState.widget?.id}
                          competitionId={competition.id}
                        >
                          <img className={bem.e('provider-logo')} src={getProviderImageUrl(provider, LogoType.LIGHT)}
                               alt={provider.name}/>
                        </OddsTrackingLink>
                      </div>
                    );
                  })}
                  {isMobile && (
                    <div className={bem.e('scroll-btn-background left')} onClick={() => scrollTable('l')}>
                      <div className={bem.e('scroll-btn left')}></div>
                    </div>
                  )}
                  {isMobile && (
                    <div className={bem.e('scroll-btn-background right')} onClick={() => scrollTable('r')}>
                      <div className={bem.e('scroll-btn right')}></div>
                    </div>
                  )}
                </div>
                {/* Event rows */}
                {eventGroup.events.map((event) => {
                  let outcomeNames = getOutcomeNames()

                  return (
                    <div className={bem.e('event-row')} key={event.id}>
                      <div className={bem.e('us-table-row__event-wrapper')}>
                        <div className={bem.e('us-table-row__cell time')}>
                          <span>
                            {DateTime.fromISO(event.userStartTime).toLocaleString({
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: false
                            })}
                          </span>
                        </div>
                        <div className={bem.e('us-table-row__odds-cell')}>
                          {/* Home team */
                            getTeamRowLayout(event, OutcomeType.HOME)
                          }
                          {/* Away team */
                            getTeamRowLayout(event, OutcomeType.AWAY)
                          }
                          {/* Draw */
                            outcomeNames && outcomeNames.names.length > 2 && getTeamRowLayout(event, OutcomeType.DRAW)
                          }
                        </div>
                      </div>
                    </div>
                  )
                })}
                <div className={bem.e('legend')}>
                  <div className={bem.e('legend__legend_item')}>
                    <div className={bem.e('legend__legend_item__oval red')}></div>
                    <div>Odds Drifting</div>
                  </div>
                  <div className={bem.e('legend__legend_item')}>
                    <div className={bem.e('legend__legend_item__oval blue')}></div>
                    <div>Odds Shortening</div>
                  </div>
                  <div className={bem.e('legend__legend_item')}>
                    <div className={bem.e('legend__legend_item__oval black')}></div>
                    <div>Best odds in Bold</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }
  }

  return <></>
}

export default OddsComparisonTable
